"use client"

import type { ReactNode } from "react"
import type { LucideIcon } from "lucide-react"
import { ChevronDown, Loader2, LockKeyhole } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"

export type AgentToolCategoryTone =
  | "general"
  | "crm_application"
  | "delivery"

type AgentToolCategoryProps = {
  title: string
  description: string
  tone: AgentToolCategoryTone
  icon: LucideIcon
  children: ReactNode
}

type AgentToolCardProps = {
  title: string
  description: string
  categoryLabel: string
  requirement?: "Required" | "Optional"
  tone: AgentToolCategoryTone
  icon: LucideIcon
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  disabled?: boolean
  isPending?: boolean
  lockedReason?: string
  toggleDisabledReason?: string
  children: ReactNode
}

const CATEGORY_STYLES: Record<AgentToolCategoryTone, {
  section: string
  icon: string
  badge: string
  toolIcon: string
}> = {
  general: {
    section: "border-slate-200 bg-slate-50/70",
    icon: "bg-slate-200 text-slate-700",
    badge: "border-slate-300 bg-slate-100 text-slate-700",
    toolIcon: "bg-slate-100 text-slate-700",
  },
  crm_application: {
    section: "border-blue-200 bg-blue-50/60",
    icon: "bg-blue-100 text-blue-700",
    badge: "border-blue-300 bg-blue-100 text-blue-800",
    toolIcon: "bg-blue-50 text-blue-700",
  },
  delivery: {
    section: "border-violet-200 bg-violet-50/60",
    icon: "bg-violet-100 text-violet-700",
    badge: "border-violet-300 bg-violet-100 text-violet-800",
    toolIcon: "bg-violet-50 text-violet-700",
  },
}

export function AgentToolCategory({
  title,
  description,
  tone,
  icon: Icon,
  children,
}: AgentToolCategoryProps) {
  const styles = CATEGORY_STYLES[tone]
  return (
    <section className={cn("space-y-3 rounded-xl border p-4", styles.section)}>
      <div className="flex items-start gap-3">
        <div className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
          styles.icon,
        )}>
          <Icon aria-hidden="true" className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <h3 className="text-balance text-sm font-semibold">{title}</h3>
          <p className="mt-0.5 text-pretty text-xs text-muted-foreground">
            {description}
          </p>
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  )
}

export function AgentToolCard({
  title,
  description,
  categoryLabel,
  requirement,
  tone,
  icon: Icon,
  checked,
  onCheckedChange,
  expanded,
  onExpandedChange,
  disabled = false,
  isPending = false,
  lockedReason,
  toggleDisabledReason,
  children,
}: AgentToolCardProps) {
  const styles = CATEGORY_STYLES[tone]
  const isLocked = Boolean(lockedReason)
  const switchDisabled = disabled || isPending || isLocked || Boolean(toggleDisabledReason)

  return (
    <Collapsible open={expanded} onOpenChange={onExpandedChange}>
      <Card className={cn("overflow-hidden", isLocked && "opacity-70")}>
        <CardHeader className="p-4">
          <div className="flex items-start gap-3">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex min-w-0 flex-1 items-start gap-3 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <div className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                  styles.toolIcon,
                )}>
                  <Icon aria-hidden="true" className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-base font-semibold">{title}</h4>
                    <Badge
                      variant="outline"
                      className={cn("text-[10px] uppercase tracking-wide", styles.badge)}
                    >
                      {categoryLabel}
                    </Badge>
                    {requirement ? (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                        {requirement}
                      </Badge>
                    ) : null}
                    {isLocked ? (
                      <LockKeyhole
                        aria-hidden="true"
                        className="h-4 w-4 text-muted-foreground"
                      />
                    ) : null}
                  </div>
                  <p className="mt-1 text-pretty text-sm text-muted-foreground">
                    {description}
                  </p>
                  {lockedReason ? (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {lockedReason}
                    </p>
                  ) : null}
                  {toggleDisabledReason ? (
                    <p className="mt-2 text-xs text-amber-700">
                      {toggleDisabledReason}
                    </p>
                  ) : null}
                </div>
              </button>
            </CollapsibleTrigger>
            <div className="flex shrink-0 items-center gap-2">
              {isPending ? (
                <Loader2
                  aria-hidden="true"
                  className="h-4 w-4 animate-spin text-muted-foreground"
                />
              ) : null}
              <Switch
                aria-label={`Enable ${title}`}
                checked={isLocked ? false : checked}
                disabled={switchDisabled}
                onCheckedChange={onCheckedChange}
              />
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
                >
                  <ChevronDown
                    aria-hidden="true"
                    className={cn(
                      "h-4 w-4 transition-transform motion-reduce:transition-none",
                      expanded && "rotate-180",
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="space-y-4 border-t bg-background p-4">
            {children}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
